import { Check, Copy } from 'react-feather';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import copy from 'copy-to-clipboard';

import { StyledLinkProps } from '../UI/Nav';
import { UIProps } from '../../theme/mixins';
import { useNotifier } from '../Notifications/NotificationProvider';
import UI from '../UI';

export const useCopyLink = (value: string) => {
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    copy(value, { format: 'text/plain' });
    setCopied(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setCopied(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [copied]);

  return [handleClick, copied] as [() => void, boolean];
};

interface CopyLinkInputProps {
  value: string;
  id?: string;
  sc?: UIProps;
}

export const CopyLinkInput = ({ value, id, sc }: CopyLinkInputProps) => {
  const { t } = useTranslation('common');
  const notifier = useNotifier();

  const inputRef = useRef<HTMLInputElement>();
  const buttonRef = useRef<HTMLButtonElement>();

  const [copy, copied] = useCopyLink(value);

  useEffect(() => {
    const input = inputRef.current;

    if (input) {
      const { paddingRight } = input.style;
      input.style.paddingRight = `${Math.max((buttonRef.current?.offsetWidth || 0) + 10, 0)}px`;

      return () => {
        input.style.paddingRight = `${paddingRight}px`;
      };
    }

    return () => {};
  });

  const handleClick = () => {
    copy();
    notifier.info(t('copy_link.copied'));
  };

  return (
    <UI.InputWrapper>
      <UI.Input
        defaultValue={value}
        id={id}
        sc={sc}
      />
      <UI.Button
        onClick={handleClick}
        sc={{ blank: true, noWrap: true, color: copied ? 'gray.500' : undefined }}
        ref={buttonRef}
        style={{ borderLeft: '1px solid rgba(0, 0, 0, .075)' }}
      >
        <UI.Icon>
          {copied ? <Check /> : <Copy />}
        </UI.Icon>
        {' '}
        {t('copy')}
      </UI.Button>
    </UI.InputWrapper>
  );
};

interface CopyLinkProps {
  value: string;
  children?: ReactNode;
  sc?: StyledLinkProps;
}

const CopyLink = ({ value, children, sc }: CopyLinkProps) => {
  const { t } = useTranslation('common');
  const notifier = useNotifier();

  const [copy, copied] = useCopyLink(value);

  const handleClick = () => {
    copy();
    notifier.info(t('copy_link.copied'));
  };

  return (
    <UI.A onClick={handleClick} title={value} sc={{ color: copied ? 'gray.500' : undefined, ...sc }}>
      <UI.Icon>
        {copied ? <Check /> : <Copy />}
      </UI.Icon>
      {' '}
      {children}
    </UI.A>
  );
};

export default CopyLink;
